import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CinematicHero = () => {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Cinematic Background - Updated Color */}
      <div className="absolute inset-0 bg-[#0b1221]">
        {/* Particles Effect */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(50)]?.map((_, i) =>
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#FFD100] rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }} />

          )}
        </div>

        {/* Golden Glow Effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FFD100]/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FFD100]/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1.5s' }} />
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        {/* Glass Morphism Card */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-12 shadow-2xl">
          {/* Logo/Company Image Placeholder */}
          <div className="flex justify-center mb-8">
            <div className="w-32 h-32 rounded-2xl overflow-hidden">
              <img
                src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1764021268/FOD_logo_mrjqxg.png"
                alt="Company Logo"
                className="w-full h-full object-contain" />

            </div>
          </div>

          {/* Main Headline - Updated Text */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight text-center">
            FIXING EMPLOYEE ONBOARDING. <span className="text-[#FFD100]">FOREVER.</span>
          </h1>

          {/* Meet The Employee Operating System */}
          <div className="mb-8 text-center">
            <p className="text-xl md:text-2xl text-[#FFD100] font-semibold mb-2">
              Meet The Employee Operating System
            </p>
            <p className="text-lg text-white/80 max-w-3xl mx-auto leading-relaxed">
              The AI-native Employee Success OS that unifies knowledge, automates workflows, and empowers global teams.
            </p>
          </div>

          {/* AI Buddy Mockup Section */}
          <div className="mb-12 max-w-4xl mx-auto">
            <div className="backdrop-blur-md bg-gradient-to-br from-white/10 to-white/5 border border-[#FFD100]/30 rounded-2xl p-8 shadow-2xl">
              <div className="flex items-start gap-6">
                {/* AI Buddy Icon */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[#FFD100] rounded-full flex items-center justify-center shadow-lg shadow-[#FFD100]/50">
                    <span className="text-3xl">🐝</span>
                  </div>
                </div>
                
                {/* AI Buddy Chat Preview */}
                <div className="flex-1">
                  <div className="mb-4">
                    <p className="text-white font-semibold text-lg mb-2">AI Buddy</p>
                    <p className="text-white/70 text-sm">Your intelligent onboarding assistant</p>
                  </div>
                  
                  {/* Sample Chat Bubbles */}
                  <div className="space-y-3">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                      <p className="text-white/90 text-sm">
                        👋 Welcome to First On Date! I'm here to guide you through your first week. What would you like to know?
                      </p>
                    </div>
                    <div className="bg-[#FFD100]/20 backdrop-blur-sm rounded-xl p-4 border border-[#FFD100]/30 ml-8">
                      <p className="text-white text-sm">
                        What's the WiFi password?
                      </p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                      <p className="text-white/90 text-sm">
                        🔐 The office WiFi is "BeeWise2024". I've also added your IT setup checklist to your dashboard!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <Button
              variant="default"
              size="xl"
              onClick={() => navigate('/login-authentication')}
              className="bg-[#FFD100] hover:bg-[#FFD100]/90 text-[#0B1220] font-semibold px-12 py-4 text-lg shadow-lg shadow-[#FFD100]/20">
              Start Free Trial
            </Button>
            <Button
              variant="outline"
              size="xl"
              onClick={() => {
                window.open('https://youtu.be/NmTYUuGiI3U', '_blank');
              }}
              className="border-2 border-white/20 text-white hover:bg-white/10 px-12 py-4 text-lg">
              Watch Demo
            </Button>
          </div>

          {/* Integration Note */}
          <div className="mb-8">
            <p className="text-white/60 text-sm mb-4 text-center">Fully integrated with Microsoft 365 & Google Workspace</p>
            <div className="flex gap-6 justify-center items-center flex-wrap">
              <div className="flex items-center gap-2 text-white/40">
                <Icon name="Mail" size={20} />
                <span className="text-sm">Teams</span>
              </div>
              <div className="flex items-center gap-2 text-white/40">
                <Icon name="FileText" size={20} />
                <span className="text-sm">SharePoint</span>
              </div>
              <div className="flex items-center gap-2 text-white/40">
                <Icon name="Cloud" size={20} />
                <span className="text-sm">OneDrive</span>
              </div>
              <div className="flex items-center gap-2 text-white/40">
                <Icon name="Calendar" size={20} />
                <span className="text-sm">Outlook</span>
              </div>
              <div className="flex items-center gap-2 text-white/40">
                <Icon name="FolderOpen" size={20} />
                <span className="text-sm">Drive</span>
              </div>
              <div className="flex items-center gap-2 text-white/40">
                <Icon name="Send" size={20} />
                <span className="text-sm">Gmail</span>
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="backdrop-blur-sm bg-white/5 border border-[#FFD100]/20 rounded-2xl p-6 hover:border-[#FFD100]/40 transition-all duration-300">
              <div className="text-5xl font-bold text-[#FFD100] mb-2">87%</div>
              <p className="text-white/70">Faster Time-to-Productivity</p>
            </div>
            <div className="backdrop-blur-sm bg-white/5 border border-[#FFD100]/20 rounded-2xl p-6 hover:border-[#FFD100]/40 transition-all duration-300">
              <div className="text-5xl font-bold text-[#FFD100] mb-2">65%</div>
              <p className="text-white/70">Reduction in First-Week Turnover</p>
            </div>
            <div className="backdrop-blur-sm bg-white/5 border border-[#FFD100]/20 rounded-2xl p-6 hover:border-[#FFD100]/40 transition-all duration-300">
              <div className="text-5xl font-bold text-[#FFD100] mb-2">€2.3M</div>
              <p className="text-white/70">Annual Savings for Enterprise</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CinematicHero;